import { Button, Checkbox, Input } from '@aqarmap/aqarmap-ui';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { useCallback, useEffect, useState } from 'react';
import PhotoCard from '~addListing/pages/ListingMediaStep/PhotoCard';
import ListingMediaHelper from '~addListing/pages/ListingMediaStep/ListingMediaHelper';
import {
  useAddListingDetailsMutation,
  useDeletePhotosMutation,
  useGetListingDetailsQuery,
  useGetUserQuery,
  useListingRulesQuery,
  useSortListingPhotosMutation,
} from '~/redux/api/userApiSlice';
import { useNavigate, useParams } from 'react-router-dom';
import { ListingDetailsType, ListingStatusEnum } from '~addListing/types';
import { RoutesEnum } from '~/constants/enums';
import Loader from '~/components/Loader';
import {
  deleteListingData,
  saveListingData,
} from '~addListing/utils/LocalStorage';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { zodResolver } from '@hookform/resolvers/zod';
import { videoSchema } from '~addListing/utils/schema';
import { useFullRoutes } from '~/hooks';

type PhotoItem = {
  id: string;
  file: File;
  photoId?: string;
};

type UploadedPhotosType = {
  id: string;
  src: string;
};

const ListingMediaStep = () => {
  const { ADD_LISTING_URL } = useFullRoutes();
  const { t } = useTranslation('app');
  const { locale, id: listingId } = useParams();
  const navigate = useNavigate();
  const [isErrorPhoto, setIsErrorPhoto] = useState(false);
  const [listingStatusId, setListingStatusId] = useState<number | null>(null);
  const [files, setFiles] = useState<PhotoItem[]>([]);
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([]);
  const [uploadedPhotos, setUploadedPhotos] = useState<UploadedPhotosType[]>(
    [],
  );
  const [deletePhotos, { isLoading: isLoadingDeletePhotos }] =
    useDeletePhotosMutation();
  const { data: listingRulesData, isLoading: isLoadingListingFees } =
    useListingRulesQuery(listingId);
  const {
    data: listingDetailsData,
    isSuccess: isListingDetailsSuccess,
    isFetching: isFetchingListingDetails,
    refetch,
  } = useGetListingDetailsQuery(listingId);
  const [addListingDetails, { isLoading: isLoadingAddListingDetails }] =
    useAddListingDetailsMutation();
  const [sortListingPhotos] = useSortListingPhotosMutation();
  const { data: userData } = useGetUserQuery();

  const listingDetails: ListingDetailsType = listingDetailsData?.listing;
  const isEmptyPhotos = uploadedPhotos.length === 0 && files.length === 0;
  const requiredPhotosNumber = listingRulesData?.required_photos;

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = uploadedPhotos.findIndex(
      (photo) => photo.id === active.id,
    );
    const newIndex = uploadedPhotos.findIndex((photo) => photo.id === over.id);

    const newOrder = arrayMove(uploadedPhotos, oldIndex, newIndex);
    setUploadedPhotos(newOrder);

    const orderedIds = newOrder.map((photo) => photo.id);
    reorderPhotosAPI({ orderedIds, activeId: active.id, newIndex, newOrder });
  };

  const reorderPhotosAPI = async (ids: any) => {
    try {
      await sortListingPhotos({
        body: { ids: ids.orderedIds },
        listingId,
      });
    } catch (err) {
      console.error('Error reordering photos:', err);
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const MAX_FILE_SIZE_MB = 30;
      const maxSizeInBytes = MAX_FILE_SIZE_MB * 1024 * 1024;

      const validFiles = acceptedFiles.filter((file) => {
        if (file.size > maxSizeInBytes) {
          alert(
            t('errorPhotosSize', {
              fileName: file.name,
              fileSize: MAX_FILE_SIZE_MB,
            }),
          );
          return false;
        }
        return true;
      });

      const newFiles: PhotoItem[] = validFiles.map((file) => ({
        id: crypto.randomUUID(),
        file,
      }));

      setFiles((prev) => [...prev, ...newFiles]);
    },
    [t],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
  });

  const { handleSubmit, register, setValue, formState } = useForm({
    defaultValues: {
      videoUrl: '',
    },
    resolver: zodResolver(videoSchema),
  });

  const onSubmit = async (data: any) => {
    if (requiredPhotosNumber > uploadedPhotos.length)
      return setIsErrorPhoto(true);

    let isSubmitSucceed = true;

    if (data.videoUrl) {
      isSubmitSucceed = false;
      const addListingDetailsData = saveListingData(`${listingId}`, data);
      const res = await addListingDetails({
        id: listingId,
        body: addListingDetailsData.data,
      });
      if ('data' in res) isSubmitSucceed = true;
      else {
        const error = res.error as any;
        const errorMessage =
          error?.data?.errors[0]?.message || t('errorOccurred');
        return alert(errorMessage);
      }
    }

    if (isSubmitSucceed && listingId) {
      if (listingStatusId === ListingStatusEnum.PENDING) {
        deleteListingData(`${listingId}`);
        window.location.href = `${process.env.REACT_APP_BASE_URL}/${locale}/${RoutesEnum.LISTING}/${listingId}-${listingDetails.section.slug}`;
      } else navigate(ADD_LISTING_URL.PUBLISH(listingId));
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    if (isChecked) setSelectedPhotos(uploadedPhotos.map((f) => f.id));
    else setSelectedPhotos([]);
  };

  const handleDeleteSelectedPhotos = () => {
    let uploadedPhotoIds = uploadedPhotos
      .filter((p) => selectedPhotos.includes(p.id))
      .map((p) => p.id)
      .filter((id) => id !== undefined) as string[];

    deletePhotos({ formData: { ids: uploadedPhotoIds }, id: listingId })
      .unwrap()
      .then((e) => {
        setUploadedPhotos((prev) =>
          prev.filter((f) => !selectedPhotos.includes(f.id)),
        );
        setSelectedPhotos([]);
        setListingStatusId(e.listing.status);
      })
      .catch((error) => {
        console.error('Error deleting photos:', error);
      });
  };

  useEffect(() => {
    if (
      !isFetchingListingDetails &&
      !isListingDetailsSuccess &&
      !isLoadingListingFees
    )
      navigate(ADD_LISTING_URL.INITIALIZE);
    else {
      let uploadedPhotosData = listingDetails?.photos.map((photo) => ({
        id: `${photo.id}`,
        src: photo.photo.thumbnails.large,
        order: photo.order,
      }));
      if (uploadedPhotosData)
        uploadedPhotosData.sort((a, b) => a.order - b.order);
      setListingStatusId(listingDetails?.status);
      setUploadedPhotos(uploadedPhotosData || []);
    }

    if (isListingDetailsSuccess) {
      const isOwner = listingDetails?.user.id === userData?.user?.id;
      const isPublisher =
        listingDetails?.publisher_user?.id === userData?.user?.id;
      if (!isOwner && !isPublisher) {
        navigate(ADD_LISTING_URL.INITIALIZE);
      }
    }
  }, [isListingDetailsSuccess, isFetchingListingDetails, isLoadingListingFees]);

  useEffect(() => {
    if (listingDetailsData && listingDetailsData.listing) {
      setValue('videoUrl', listingDetailsData.listing.video_url || '');
    }
  }, [listingDetailsData]);

  useEffect(() => {
    refetch();
  }, []);

  if (isFetchingListingDetails) return <Loader />;

  return (
    <div className="flex flex-col gap-4x w-full">
      <div className="flex flex-col gap-3x">
        <p className="text-title_2">{t('videosAndPhotos')}</p>
        <ListingMediaHelper className="md:hidden" />
      </div>
      <div className="flex flex-col gap-4x md:gap-5x max-w-[636px]">
        <div
          className={`flex flex-col ${files.length > 0 ? 'gap-3x' : 'gap-2x'}`}
        >
          <p className="text-title_3">{t('propertyPhotos')}</p>
          <div className="flex flex-col gap-2x">
            {uploadedPhotos.length > 0 && (
              <div className="w-full flex justify-between items-center">
                <Checkbox
                  label={{
                    text: t('selectAll'),
                    className: 'text-link text-black',
                  }}
                  size="medium"
                  field={{
                    name: 'selectAll',
                    checked: selectedPhotos.length === uploadedPhotos.length,
                    onChange: handleSelectAll,
                  }}
                />
                <button
                  className={`text-link ${selectedPhotos.length === 0 ? 'text-gray' : 'text-red_dark'}`}
                  onClick={() => handleDeleteSelectedPhotos()}
                  disabled={selectedPhotos.length === 0}
                >
                  {isLoadingDeletePhotos ? (
                    <span className="loading loading-spinner loading-sm bg-red_light"></span>
                  ) : (
                    t('delete')
                  )}
                </button>
              </div>
            )}
            <div className="flex flex-wrap gap-2x justify-start">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={uploadedPhotos.map((photo) => photo.id)}
                  strategy={rectSortingStrategy}
                >
                  {uploadedPhotos?.map((photo) => (
                    <PhotoCard
                      key={photo.id}
                      file={{ id: photo?.id, file: new File([], '') }}
                      isChecked={selectedPhotos.includes(`${photo.id}`)}
                      files={files}
                      setFiles={setFiles}
                      setSelectedPhotos={setSelectedPhotos}
                      setUploadedPhotos={setUploadedPhotos}
                      uploadedPhotos={uploadedPhotos}
                      src={photo.src}
                      isUploaded={true}
                      setListingStatusId={setListingStatusId}
                      id={photo.id}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              {files.length > 0 &&
                files.map((file) => (
                  <PhotoCard
                    key={file.id}
                    file={file}
                    isChecked={selectedPhotos.includes(file.id)}
                    files={files}
                    setFiles={setFiles}
                    setSelectedPhotos={setSelectedPhotos}
                    setUploadedPhotos={setUploadedPhotos}
                    uploadedPhotos={uploadedPhotos}
                    src={URL.createObjectURL(file.file)}
                    setListingStatusId={setListingStatusId}
                  />
                ))}
              <div
                {...getRootProps()}
                className={`${isEmptyPhotos ? 'flex-1 h-[91px]' : 'flex-col w-full sm:w-[200px] h-[156px]'} -order-1 sm:order-none p-1.5x border border-gray  rounded-[10px] flex gap-3x justify-center items-center cursor-pointer`}
              >
                <input {...getInputProps()} />
                <div className="h-[60px] w-[108px] relative">
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/images/placeholder_images.png`}
                    alt="placeholder"
                  />
                </div>
                <div className="w-max flex flex-col gap-1">
                  <p className="text-link text-blue">
                    {isDragActive ? t('dragPhotosHere') : t('addPhotosHere')}
                  </p>
                  <p className="text-caption text-gray__dark_1">
                    {t('maxFileSize')}
                  </p>
                </div>
              </div>
            </div>
          </div>
          {isErrorPhoto && (
            <p className="text-link text-red_dark">
              {t('requiredPhotos', {
                count: listingRulesData?.required_photos || 0,
              })}
            </p>
          )}
        </div>
        <div className="flex flex-col">
          <div className="flex items-center gap-1">
            <p className="text-title_3">{t('videoLink')}</p>
            <p className="text-body_1 text-gray__dark_2">({t('optional')})</p>
          </div>
          <Input
            field={{
              placeholder: t('youtubeLink'),
              className:
                'h-[40px] w-full placeholder:!text-gray__dark_1 text-gray__dark_2',
              ...register('videoUrl'),
            }}
            error={
              formState.errors.videoUrl
                ? t(`${formState.errors.videoUrl.message}`)
                : ''
            }
          />
        </div>
        <div>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="primary"
            size="medium"
            className="w-[135px]"
            isLoading={isLoadingAddListingDetails}
          >
            {t('follow')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ListingMediaStep;
